﻿import { Space, TagProps } from "antd";
import { observer } from "mobx-react";
import Map from "ol/Map";
import React, { CSSProperties, ReactNode } from "react";
import { MapControlsState } from "../models/MapControlsState";
import DepthLayerField, { Props as DepthLayerProps } from "./DepthLayerField";
import MapAttributionField, { Props as MapAttributionFieldProps } from "./MapAttributionField";
import MapScaleLine, { Props as MapScaleLineProps } from "./MapScaleLine";
import MapZoomControl, { Props as MapZoomControlProps } from "./MapZoomControl";
import MousePositionField, { Props as MousePositionFieldProps } from "./MousePositionField";
import PickReportControl, { Props as PickReportProps } from "./pickReport/PickReportControl";

export interface Props extends Pick<TagProps, "color"> {
    map: Map | undefined;
    state?: MapControlsState,
    zoom?: Omit<MapZoomControlProps, "map" | "mobile" | "color"> | boolean;
    scale?: Omit<MapScaleLineProps, "map" | "mobile" | "color"> | boolean;
    attribution?:
    | Omit<MapAttributionFieldProps, "map" | "mobile" | "color">
    | boolean;
    mouse?: Omit<MousePositionFieldProps, "map" | "mobile" | "color"> | boolean;
    pick?: Omit<PickReportProps, "map" | "mobile" | "color">;
    style?: CSSProperties;
    depth?: Omit<DepthLayerProps, "map" | "mobile" | "color"> | boolean;
    mobile?: boolean;
    additionalTopControls?: ReactNode[];
    additionalBottomControls?: ReactNode[];
    children?: ReactNode[] | ReactNode;
}

const containerStyle: CSSProperties = {
    pointerEvents: "none",
    position: "absolute",
};

const controlStyle: CSSProperties = {
    position: "relative",
};

const inset = 8;

const MapDefaultControls: React.FC<Props> = ({
    state,
    zoom = true,
    scale = true,
    attribution = true,
    mouse = true,
    depth = true,
    pick,
    style,
    additionalTopControls,
    additionalBottomControls,
    children,
    ...rest
}) => {
    if (zoom === true) zoom = {};

    if (scale === true) scale = {};

    if (attribution === true) attribution = {};

    if (mouse === true) mouse = {};

    if (depth === true) depth = {};

    return (
        <>
            <div
                className="control-main-container"
                style={{
                    inset,
                    ...containerStyle,
                    ...style,
                }}
            >
                <Space
                    className="control-top-left-container"
                    direction="vertical"
                    size={inset}
                    style={{
                        top: 0,
                        display: "relative",
                        ...containerStyle,
                    }}
                >
                    {zoom && state?.showZoom != false && (
                        <MapZoomControl
                            {...zoom}
                            {...rest}
                            key="zoom"
                            style={{
                                ...zoom.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {pick && (
                        <PickReportControl
                            {...pick}
                            {...rest}
                            key="pick"
                            style={{
                                ...pick.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {additionalTopControls}
                </Space>

                {children}

                <div
                    className="control-bottom-container"
                    style={{
                        bottom: 0,
                        display: "flex",
                        ...containerStyle,
                    }}
                >
                    {attribution && (
                        <MapAttributionField
                            {...attribution}
                            {...rest}
                            key="attribution"
                            style={{
                                order: 10,
                                ...attribution.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {mouse && state?.showCoordinate != false && (
                        <MousePositionField
                            {...mouse}
                            {...rest}
                            key="mouse"
                            style={{
                                order: 20,
                                ...mouse.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {scale && state?.showScale != false && (
                        <MapScaleLine
                            {...scale}
                            {...rest}
                            key="scale"
                            style={{
                                order: 30,
                                ...scale.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {depth && (
                        <DepthLayerField
                            {...depth}
                            {...rest}
                            key="depth"
                            style={{
                                order: 40,
                                ...depth.style,
                                ...controlStyle,
                            }}
                        />
                    )}

                    {additionalBottomControls}
                </div>
            </div>
        </>
    );
};

export default observer(MapDefaultControls);
